// utils/invoiceFetcher.js
import React from "react";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";

/**
 * Fetches invoices for a specific facility
 * @param {string} facilityId - The ID of the facility to fetch invoices for
 * @param {Object} options - Optional parameters for the request
 * @param {number} options.page - Page number for pagination (default: 1)
 * @param {number} options.limit - Number of invoices per page (default: 10)
 * @param {string} options.status - Filter by status (pending, paid, overdue)
 * @param {boolean} options.showToast - Whether to show toast messages (default: true)
 * @returns {Promise<Object>} Response object with invoices data
 */
export const getFacilityInvoices = async (facilityId, options = {}) => {
  const { page = 1, limit = 10, status = null, showToast = true } = options;

  // Validate facilityId
  if (!facilityId) {
    const error = "Facility ID is required";
    if (showToast) {
      toastify(error, "error");
    }
    return {
      success: false,
      error,
      invoices: [],
      pagination: null,
      summary: null,
    };
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status) {
      queryParams.append("status", status);
    }

    const url = `/api/core/billing/facility/${facilityId}/invoices?${queryParams.toString()}`;
    console.log(`Making request to: ${url}`);

    const response = await makeRequest2(url, "GET");
    console.log("Raw API response:", response);

    if (response && response.success && response.data) {
      // The actual data is nested inside response.data
      const responseData = response.data;

      // Ensure invoices is always an array, even if API returns undefined or null
      const invoices = Array.isArray(responseData.invoices)
        ? responseData.invoices
        : [];

      if (showToast && invoices.length > 0) {
        toastify(`Loaded ${invoices.length} invoices successfully`, "success");
      }

      // Add facilityId to each invoice for consistency
      const invoicesWithFacilityId = invoices.map((invoice) => ({
        ...invoice,
        facilityId: facilityId,
      }));

      return {
        success: true,
        invoices: invoicesWithFacilityId,
        pagination: responseData.pagination || null,
        summary: responseData.summary || null,
      };
    } else {
      const errorMsg =
        response?.error || response?.message || "Unknown error occurred";
      console.error("API response error:", errorMsg);

      if (showToast) {
        toastify("Failed to load invoices: " + errorMsg, "error");
      }

      return {
        success: false,
        error: errorMsg,
        invoices: [],
        pagination: null,
        summary: null,
      };
    }
  } catch (error) {
    console.error(`Error fetching invoices for facility ${facilityId}:`, error);
    const errorMsg = error.message || "Network error occurred";

    if (showToast) {
      toastify("Error fetching invoices: " + errorMsg, "error");
    }

    return {
      success: false,
      error: errorMsg,
      invoices: [],
      pagination: null,
      summary: null,
    };
  }
};

/**
 * Fetches invoices for multiple facilities
 * @param {Array} facilities - Array of facility objects with _id property
 * @param {Object} options - Optional parameters for the request
 * @returns {Promise<Array>} Array of all invoices from all facilities
 */
export const getAllFacilitiesInvoices = async (facilities, options = {}) => {
  const allInvoices = [];
  const { showToast = true } = options;

  // Validate input
  if (!Array.isArray(facilities) || facilities.length === 0) {
    if (showToast) {
      toastify("No facilities provided", "warning");
    }
    return [];
  }

  try {
    const invoicePromises = facilities.map(async (facility) => {
      // Validate facility object
      if (!facility || !facility._id) {
        console.warn("Invalid facility object:", facility);
        return [];
      }

      const result = await getFacilityInvoices(facility._id, {
        ...options,
        showToast: false, // Don't show individual toasts
      });

      if (result.success) {
        // Add facility name to each invoice
        return result.invoices.map((invoice) => ({
          ...invoice,
          facilityName: facility.name || "Unknown Facility",
          facilityId: facility._id,
        }));
      } else {
        console.error(
          `Failed to fetch invoices for facility ${facility.name || facility._id}:`,
          result.error,
        );
        return [];
      }
    });

    const results = await Promise.all(invoicePromises);
    results.forEach((facilityInvoices) => {
      if (Array.isArray(facilityInvoices)) {
        allInvoices.push(...facilityInvoices);
      }
    });

    if (showToast) {
      toastify(
        `Loaded ${allInvoices.length} invoices from ${facilities.length} facilities`,
        "success",
      );
    }

    return allInvoices;
  } catch (error) {
    console.error("Error fetching invoices from all facilities:", error);
    if (showToast) {
      toastify("Error fetching invoices: " + error.message, "error");
    }
    return [];
  }
};

/**
 * Hook-style function for React components to fetch and manage facility invoices
 * @param {string} facilityId - The facility ID to fetch invoices for
 * @param {Object} options - Optional parameters
 * @returns {Object} Object with invoices data and loading state
 */
export const useFacilityInvoices = (facilityId, options = {}) => {
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [pagination, setPagination] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchInvoices = React.useCallback(async () => {
    if (!facilityId) {
      setInvoices([]);
      setPagination(null);
      setSummary(null);
      setError("No facility ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getFacilityInvoices(facilityId, options);

      if (result.success) {
        setInvoices(result.invoices || []);
        setPagination(result.pagination);
        setSummary(result.summary);
        setError(null);
      } else {
        setError(result.error);
        setInvoices([]);
        setPagination(null);
        setSummary(null);
      }
    } catch (err) {
      setError(err.message);
      setInvoices([]);
      setPagination(null);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [facilityId, JSON.stringify(options)]);

  React.useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return {
    invoices,
    loading,
    pagination,
    summary,
    error,
    refetch: fetchInvoices,
  };
};
